import { useState, useEffect, useCallback } from "react";

// Voices to actively avoid (robotic or male)
const AVOID_PATTERN = /\b(zira|david|mark|richard|james|daniel|fred|george|tom|bob|bruce|mike)\b/i;

// Female voices ranked best → acceptable (checked in order)
const PREFERRED_FEMALE = [
    "ava",          // macOS — very natural
    "allison",      // macOS
    "samantha",     // macOS / iOS default
    "aria",         // Windows Edge Neural
    "michelle",     // Windows Edge Neural
    "monica",       // Windows Edge Neural
    "jenny",        // Windows Edge Neural
    "karen",        // macOS Australian (still pleasant)
    "moira",        // macOS Irish
    "tessa",        // macOS South African
    "fiona",        // macOS Scottish
];

const getFreshVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    const enUS = voices.filter(v => v.lang === "en-US");

    // Log once so you can see exactly what's available in the browser console
    console.log("[ReadAloud] en-US voices:", enUS.map(v => v.name));

    // 1. Neural / Natural voices — sound the most human (Edge, Chrome on Windows)
    const neural = enUS.find(v => /natural|neural/i.test(v.name) && !AVOID_PATTERN.test(v.name));
    if (neural) {
        console.log("[ReadAloud] Using neural voice:", neural.name);
        return neural;
    }

    // 2. Walk through preferred female names in priority order
    for (const name of PREFERRED_FEMALE) {
        const match = enUS.find(v => v.name.toLowerCase().includes(name));
        if (match) {
            console.log("[ReadAloud] Using preferred voice:", match.name);
            return match;
        }
    }

    // 3. Any en-US voice that isn't on the avoid list
    const safe = enUS.find(v => !AVOID_PATTERN.test(v.name));
    if (safe) {
        console.log("[ReadAloud] Using safe fallback:", safe.name);
        return safe;
    }

    // 4. Absolute fallback — any English voice
    const any = voices.find(v => v.lang.startsWith("en"));
    console.log("[ReadAloud] Using last-resort voice:", any?.name);
    return any || null;
};

const useReadAloud = () => {
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [spokenText, setSpokenText] = useState("");
    const [currentCharIndex, setCurrentCharIndex] = useState(0);

    // Pre-warm the voice list so getFreshVoice() works on first click
    useEffect(() => {
        const warm = () => {};
        window.speechSynthesis.addEventListener("voiceschanged", warm);
        window.speechSynthesis.getVoices();
        return () => {
            window.speechSynthesis.removeEventListener("voiceschanged", warm);
            window.speechSynthesis.cancel();
        };
    }, []);

    const speak = useCallback((text) => {
        if (!text || !window.speechSynthesis) return;
        window.speechSynthesis.cancel();

        const cleanText = text.trim();
        const utterance = new SpeechSynthesisUtterance(cleanText);

        const voice = getFreshVoice();
        if (voice) utterance.voice = voice;
        utterance.lang = "en-US";
        // Neural voices sound best at rate 1.0 — non-neural slightly slower at 0.88
        utterance.rate = voice && /natural|neural/i.test(voice.name) ? 1.0 : 0.88;
        utterance.pitch = 1.0;
        utterance.volume = 1;

        utterance.onstart = () => {
            setIsSpeaking(true);
            setSpokenText(cleanText);
            setCurrentCharIndex(0);
        };
        utterance.onboundary = (event) => {
            if (event.name === "word") setCurrentCharIndex(event.charIndex);
        };
        utterance.onend = () => {
            setIsSpeaking(false);
            setSpokenText("");
            setCurrentCharIndex(0);
        };
        utterance.onerror = () => {
            setIsSpeaking(false);
            setSpokenText("");
            setCurrentCharIndex(0);
        };

        // 50ms gap after cancel — Chrome drops the voice assignment without this
        setTimeout(() => window.speechSynthesis.speak(utterance), 50);
    }, []);

    const stop = useCallback(() => {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
        setSpokenText("");
        setCurrentCharIndex(0);
    }, []);

    return { isSpeaking, speak, stop, spokenText, currentCharIndex };
};

export default useReadAloud;
